// Renaming a member touches more than the one field.
//
// Two things are keyed off a member's name rather than their id, and both go
// quietly wrong if only `name` is written:
//
//   player.lastName          — stored separately, and what the Roster sorts on
//                              and householdGroups builds a family name from.
//                              Left behind, a corrected surname still files
//                              the member under the old one.
//   preferredPartners        — free text on OTHER members, matched against
//                              names (see validation.getPreferredPartners).
//                              Rename someone and every request to play with
//                              them stops resolving, with nothing on screen
//                              to say so.
//
// This file works out the full set of writes a rename implies. It returns
// them rather than applying them, so the caller can show what it is about to
// do and the whole thing stays testable.

export function derivedLastName(name) {
  return String(name || "").trim().split(/\s+/).slice(-1)[0] || "";
}

// A lastName that matches the last word of the old name was derived from it,
// so it follows the rename. One that differs was set deliberately — an
// imported "van der Berg" against a name ending "Berg" — and is left alone,
// since re-deriving it would throw away the more accurate value.
export function nextLastName(player, newName) {
  const stored = String(player?.lastName || "").trim();
  if (!stored || stored === derivedLastName(player?.name)) {
    return derivedLastName(newName);
  }
  return stored;
}

// Rewrites one preferredPartners string, which is a free-text list separated
// by commas, semicolons, ampersands or slashes — the same separators
// getPreferredPartners splits on. Only whole entries that match the old name
// are replaced: a partial or fuzzy match is the matcher's business, not
// something to rewrite someone's typing over.
export function renameInPartners(text, oldName, newName) {
  const raw = String(text || "");
  if (!raw.trim()) return raw;
  const target = String(oldName || "").trim().toLowerCase();
  if (!target) return raw;

  let changed = false;
  // The separators are kept so the member's own punctuation and spacing
  // survive the rewrite.
  const rewritten = raw.replace(/[^,;&/]+/g, part => {
    if (part.trim().toLowerCase() !== target) return part;
    changed = true;
    // Preserve whatever padding sat around the entry.
    const [, before = "", , after = ""] = part.match(/^(\s*)(.*?)(\s*)$/) || [];
    return `${before}${newName}${after}`;
  });

  return changed ? rewritten : raw;
}

// Every write a rename implies: the member themselves, plus anyone whose
// preferred partners named them. `partners` is empty when nobody referred to
// them, which is the usual case.
//
// The member is passed in rather than looked up by id, so an incomplete
// roster can only cost the partner rewrites — it can never swallow the
// rename itself and leave the form looking like it saved.
//
// Returns null when the name is blank or unchanged, so the caller has one
// thing to check before writing anything.
export function planRename(player, players, newName) {
  const list = Array.isArray(players) ? players : [];
  const trimmed = String(newName || "").trim().replace(/\s+/g, " ");
  if (!player || !trimmed || trimmed === player.name) return null;

  const partners = [];
  list.forEach(p => {
    if (p.id === player.id || !p.preferredPartners) return;
    const next = renameInPartners(p.preferredPartners, player.name, trimmed);
    if (next === p.preferredPartners) return;
    partners.push({ id: p.id, name: p.name, changes: { preferredPartners: next } });
  });

  return {
    player: {
      id: player.id,
      changes: { name: trimmed, lastName: nextLastName(player, trimmed) },
    },
    partners,
    oldName: player.name,
    newName: trimmed,
  };
}

// Anyone else already going by this name. A club can genuinely have two
// members with the same name, so this is a caution to show, never a refusal.
export function duplicateNames(players, playerId, newName) {
  const target = String(newName || "").trim().toLowerCase();
  if (!target) return [];
  return (Array.isArray(players) ? players : [])
    .filter(p => p.id !== playerId && String(p.name || "").trim().toLowerCase() === target);
}
